import { useEffect, useState } from 'react';
import {
  discoveryApiRef,
  fetchApiRef,
  identityApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import type { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { makeFieldSchema } from '@backstage/plugin-scaffolder-react';
import TextField from '@material-ui/core/TextField';
import FormHelperText from '@material-ui/core/FormHelperText';

export const TechneCidrFieldSchema = makeFieldSchema({
  output: z => z.string(),
  uiOptions: z =>
    z.object({
      role: z
        .enum([
          'vpn',
          'infra',
          'pods',
          'subnetApi',
          'subnetWorkers',
          'subnetLb',
          'subnetDb',
        ])
        .optional(),
    }),
});

type NetworkError = { field: string; message: string };

const ROLE_TO_FIELD: Record<string, string> = {
  vpn: 'vpnCidr',
  infra: 'vcnInfraCidr',
  pods: 'vcnPodsCidr',
  subnetApi: 'subnetApiEndpointCidr',
  subnetWorkers: 'subnetWorkersCidr',
  subnetLb: 'subnetLoadBalancerCidr',
  subnetDb: 'subnetDatabaseCidr',
};

function planFromForm(formData: Record<string, unknown>) {
  return {
    vpnCidr: String(formData.vpnCidr ?? ''),
    vcnInfraCidr: String(formData.vcnInfraCidr ?? ''),
    vcnPodsCidr: String(formData.vcnPodsCidr ?? ''),
    subnetApiEndpointCidr: String(formData.subnetApiEndpointCidr ?? ''),
    subnetWorkersCidr: String(formData.subnetWorkersCidr ?? ''),
    subnetLoadBalancerCidr: String(formData.subnetLoadBalancerCidr ?? ''),
    subnetDatabaseCidr: String(formData.subnetDatabaseCidr ?? ''),
  };
}

export async function validateTechneCidrPlan(input: {
  discoveryBaseUrl: string;
  token?: string;
  fetch: typeof fetch;
  formData: Record<string, unknown>;
  region?: string;
}): Promise<{ ok: boolean; errors: NetworkError[] }> {
  const params = new URLSearchParams();
  const region = String(input.region ?? input.formData.ociRegion ?? '').trim();
  if (region) params.set('region', region);
  const qs = params.toString();
  const response = await input.fetch(
    `${input.discoveryBaseUrl}/discovery/validate-network${qs ? `?${qs}` : ''}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(input.token ? { Authorization: `Bearer ${input.token}` } : {}),
      },
      body: JSON.stringify(planFromForm(input.formData)),
    },
  );
  if (!response.ok) {
    const payload = (await response.json().catch(() => ({}))) as {
      error?: string;
    };
    return {
      ok: false,
      errors: [
        {
          field: 'vcnInfraCidr',
          message:
            payload.error ||
            `Falha ao validar CIDRs na Oracle (${response.status})`,
        },
      ],
    };
  }
  return (await response.json()) as { ok: boolean; errors: NetworkError[] };
}

export function TechneCidrField(
  props: FieldExtensionComponentProps<string, { role?: string }>,
) {
  const {
    onChange,
    rawErrors,
    required,
    schema: { title, description },
    formData,
    uiSchema,
    formContext,
  } = props;
  const nested = (uiSchema?.['ui:options'] ?? {}) as { role?: string };
  const role = String(nested.role ?? uiSchema?.role ?? '');
  const fieldName = ROLE_TO_FIELD[role] ?? 'vcnInfraCidr';
  const rootFormData = (formContext?.formData ?? {}) as Record<string, unknown>;

  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const identityApi = useApi(identityApiRef);

  const [oracleError, setOracleError] = useState<string | null>(null);
  const [derivedPreview, setDerivedPreview] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const value = String(formData ?? '').trim();
    if (!value) {
      setOracleError(null);
      return () => {
        cancelled = true;
      };
    }

    async function run() {
      try {
        const baseUrl = await discoveryApi.getBaseUrl('oracle-providers');
        const { token } = await identityApi.getCredentials();
        const result = await validateTechneCidrPlan({
          discoveryBaseUrl: baseUrl,
          token,
          fetch: (url, init) => fetchApi.fetch(url, init),
          formData: { ...rootFormData, [fieldName]: formData },
        });
        if (cancelled) return;
        const mine = result.errors.filter(e => e.field === fieldName);
        setOracleError(mine[0]?.message ?? null);
        if (role === 'infra' && value) {
          const params = new URLSearchParams({ infraCidr: value });
          const derivedRes = await fetchApi.fetch(
            `${baseUrl}/discovery/derive-subnets?${params}`,
            { headers: token ? { Authorization: `Bearer ${token}` } : {} },
          );
          if (cancelled) return;
          if (derivedRes.ok) {
            const derived = (await derivedRes.json()) as {
              subnetApiEndpointCidr?: string;
              subnetWorkersCidr?: string;
              subnetLoadBalancerCidr?: string;
              subnetDatabaseCidr?: string;
            };
            setDerivedPreview(
              `Subnets: api ${derived.subnetApiEndpointCidr} · workers ${derived.subnetWorkersCidr} · lb ${derived.subnetLoadBalancerCidr} (pública) · db ${derived.subnetDatabaseCidr} · pods = o /19 de pods inteiro`,
            );
          } else {
            setDerivedPreview(null);
          }
        } else {
          setDerivedPreview(null);
        }
      } catch (err) {
        if (cancelled) return;
        setOracleError(
          err instanceof Error ? err.message : 'Falha ao validar na Oracle',
        );
      }
    }
    const handle = window.setTimeout(() => void run(), 1500);
    return () => {
      cancelled = true;
      window.clearTimeout(handle);
    };
  }, [
    formData,
    fieldName,
    rootFormData.vpnCidr,
    rootFormData.vcnInfraCidr,
    rootFormData.vcnPodsCidr,
    rootFormData.subnetApiEndpointCidr,
    rootFormData.subnetWorkersCidr,
    rootFormData.subnetLoadBalancerCidr,
    rootFormData.subnetDatabaseCidr,
    rootFormData.ociRegion,
    discoveryApi,
    fetchApi,
    identityApi,
  ]);

  const errorText =
    oracleError ||
    (rawErrors?.length ? String(rawErrors[0]) : undefined);

  return (
    <>
      <TextField
        fullWidth
        margin="normal"
        required={required}
        label={title}
        value={formData ?? ''}
        onChange={event => onChange(event.target.value)}
        error={Boolean(errorText)}
        helperText={errorText || description}
        placeholder={
          role === 'vpn'
            ? '10.19.0.0/16'
            : role === 'infra' || role === 'pods'
              ? '10.131.0.0/19'
              : '10.120.0.0/24'
        }
      />
      {oracleError ? null : (
        <FormHelperText>
          {derivedPreview ||
            'Validado contra VCNs já existentes na Oracle. Se conflitar, escolha outro /19.'}
        </FormHelperText>
      )}
    </>
  );
}
